import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getAccessToken } from '../../lib/tokenStore';
import StudentConsentModal from '../../features/auth/components/StudentConsentModal';

/**
 * Decodes a JWT payload without verifying the signature (client-side only).
 * Returns the decoded payload or null if malformed.
 */
const decodeJwt = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(window.atob(base64));
  } catch {
    return null;
  }
};

const isTokenExpired = (token) => {
  const payload = decodeJwt(token);
  if (!payload?.exp) return true;
  // Add a 10-second buffer to account for clock skew
  return payload.exp * 1000 < Date.now() - 10_000;
};

const ProtectedRoute = ({ children, roles }) => {
  const { user, updateUser } = useAuth();
  const location = useLocation();

  // The access token lives in memory (tokenStore). Check it is present and not expired.
  const token = getAccessToken();

  if (!user || !token || isTokenExpired(token)) {
    // AuthContext will have already cleared state.
    // The user will go through the silent refresh on next load.
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Bypass Prevention: Student must accept terms before accessing ANY protected route
  if (user?.role === 'student' && !user?.consentAcceptedAt) {
    return (
      <StudentConsentModal
        onAccepted={(updatedUser) => {
          if (updatedUser) {
            updateUser(updatedUser);
          } else {
            updateUser({ consentAcceptedAt: new Date().toISOString() });
          }
        }}
      />
    );
  }

  // Role-based guard
  if (roles && roles.length > 0) {
    if (!roles.includes(user?.role)) {
      return <Navigate to="/feed" replace />;
    }
  }

  return children;
};

export default ProtectedRoute;
